import React, { useState } from 'react';

import { PageHeader, notification } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { removeStatementLog } from '@libs/NwLogs';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { useDeleteInvoice } from '@hooks/API/UseInvoices';
import useDepartments from '@hooks/CachedLists/UseDepartments';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import { NwLinkButton } from '@components/Gui/NwButton';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import StyleVariables from '@components/Gui/StyleVariables';
import ChangeAccountDrawer from '@components/InvoicesAndCreditNotes/Common/ChangeAccountCodeDrawer';

import ChangeDepartmentDrawer from './Drawers/ChangeDepartmentDrawer';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const InvoiceHeader = styled.div`
    z-index: 1030;
    top: ${(props) => (props.$inDrawer ? '0' : '5rem')};
    width: 100%;
    position: ${(props) => (props.$inDrawer ? 'relative' : 'sticky')};

    .ant-page-header {
        margin: 1rem;
        padding: 0;
        border-bottom: ${(props) => props.theme.DefaultBorder};
        height: 6rem;

        .invoice-header-wrapper {
            padding: 10px 0 0 340px;

            span.invoice-label {
                position: absolute;
                top: 0;
                left: 0;
                width: 300px;
                padding: 1rem 1.5rem;
                font-weight: 300;
                height: 6rem;
                font-size: ${(props) => props.theme.FontSizeSm};
                text-transform: uppercase;
                border-right: ${(props) => props.theme.DefaultBorder};
            }

            h1 {
                font-size: ${(props) => props.theme.FontSizeLg};
                margin-bottom: 0;
            }

            .department-info {
                font-size: ${(props) => props.theme.FontSizeBase};
                font-weight: normal;
                margin-top: 0.25rem;
                line-height: 1.2;
            }
        }

        .ant-page-header-heading-extra {
            padding: 10px 20px 0 0;
        }
    }

    .confirm-button {
        color: ${StyleVariables.PrimaryColor};
        text-decoration: underline;
        cursor: pointer;
        user-select: none;
        font-size: 14px;
        font-weight: 500;
    }
`;

const InvoicePageHeader = ({
    invoiceData,
    handleShowEditDrawer,
    inDrawer,
    locked,
    onDelete,
    onShowCreditNoteDrawer,
}) => {
    const { mutateAsync: onDeleteInvoice } = useDeleteInvoice();
    const [showChangeAccountCodeDrawer, setShowChangeAccountCodeDrawer] = useState(false);
    const [showChangeDepartmentDrawer, setShowChangeDepartmentDrawer] = useState(false);
    const departments = useDepartments();

    const configurations = useConfigurations();
    const accounts = [];
    if (configurations && configurations.COA) {
        const COAjs = JSON.parse(configurations.COA);
        if (COAjs.accounts && COAjs.accounts.length > 0) {
            accounts.push(...COAjs.accounts);
        }
    }
    const enableCOA = accounts.length > 0;

    // Get department name for the invoice
    const getDepartmentName = () => {
        if (!invoiceData.DepartmentCreated) return null;
        const department = departments.find((dept) => dept.id === invoiceData.DepartmentCreated);
        return department ? department.name : null;
    };

    // Check if there are multiple departments in the invoice rows
    const getDepartmentsInInvoice = () => {
        const departments = new Set();
        invoiceData.Rows.forEach((item) => {
            if (item.Statement && item.Statement.DepartmentCreated) {
                departments.add(item.Statement.DepartmentCreated);
            }
        });
        return Array.from(departments);
    };

    const departmentsInInvoice = getDepartmentsInInvoice();
    const canChangeDepartment = departmentsInInvoice.length > 1;

    const usedIDs = [];
    const tempInvoiceData = invoiceData.Rows.map((item, index) => {
        const statement = item.Statement;
        if (!statement)
            return {
                key: index,
                index: item.ID,
                Guid: item.GroupGuid,
                RowIds: [{ ID: item.ID, Type: 'Text' }],
                Type: 'Text',
            };
        return {
            key: index,
            index: item.ID,
            Guid: item.GroupGuid,
            RowIds: [{ ID: item.ID, Type: 'Statement' }],
            Type: 'Statement',
        };
    });
    const groupedInvoiceData = tempInvoiceData
        .map((row) => {
            if (row.Guid === null) return row;
            if (usedIDs.filter((guid) => guid === row.Guid).length === 0) {
                usedIDs.push(row.Guid);
                const group = tempInvoiceData.filter((el) => el.Guid === row.Guid);
                return {
                    ...row,
                    RowIds: group.map((row) => ({
                        ID: row.index,
                        Type: row.Type,
                    })),
                };
            }
            return null;
        })
        .filter((el) => el !== null);

    const onDeleteWholeInvoice = async () => {
        try {
            await onDeleteInvoice(invoiceData.ID);
            removeStatementLog(invoiceData.ID);
            onDelete();
        } catch (e) {
            notification.error({
                message: 'Error deleting invoice',
                description: e?.message || 'Unknown error',
            });
        }
    };

    const getInvoiceLabel = () => {
        if (invoiceData.TypeName === 'Model') {
            return 'Model Expenses Invoice';
        }
        if (invoiceData.TypeName === 'Agency') {
            return 'Agency Commissions Invoice';
        }
        return 'Client Invoice';
    };

    const handleCreateCreditNote = () => {
        // if (invoiceData.CreditNotes.length > 0) {
        //     onShowCreditNoteDrawer(invoiceData.CreditNotes[0].ID)
        // } else {
        // create new credit note
        Axios.post(`/CreditNotes/CreateFromInvoice/${invoiceData.ID}`).then((res) => {
            onShowCreditNoteDrawer(res);
        });
        //}
    };

    const dropdownActions = [
        {
            name: '',
            key: 'edit',
            disabled: locked,
            pinned: true,
            icon: byPrefixAndName.fasl['pen'],
            action: () => handleShowEditDrawer(),
        },
        {
            name: 'Delete',
            key: 'delete',
            icon: byPrefixAndName.fasl['trash'],
            type: 'delete',
            confirmationLabel: 'You are deleting the invoice. All related entries will be reverted to billable status.',
            confirmationButtonLabel: 'DELETE',
            confirmationButtonIcon: byPrefixAndName.fasl['trash-alt'],
            confirmationPlacement: 'bottomLeft',
            disabled: locked,
            action: () => onDeleteWholeInvoice(),
        },
        {
            name: 'Create Credit Note',
            disabled: !invoiceData.Number || invoiceData.CreditNoteIDs.length > 0 || invoiceData.TypeName !== 'Client',
            key: 'creditnote',
            action: () => handleCreateCreditNote(),
        },
    ];

    if (enableCOA) {
        dropdownActions.push({
            name: 'Set Account Code on all lines',
            key: 'coa',
            action: () => setShowChangeAccountCodeDrawer(true),
        });
    }

    return (
        <>
            <InvoiceHeader $inDrawer={inDrawer}>
                <PageHeader
                    title={
                        <div className="invoice-header-wrapper">
                            <span className="invoice-label">{getInvoiceLabel()}</span>
                            <h1>
                                <strong>{getContactName(invoiceData.ContactID)}</strong> <em>-</em> {invoiceData.Name}
                            </h1>
                            {getDepartmentName() && (
                                <div className="department-info">
                                    Department: <strong>{getDepartmentName()}</strong>
                                    {canChangeDepartment && (
                                        <NwLinkButton
                                            disabled={locked}
                                            onClick={() => setShowChangeDepartmentDrawer(true)}
                                            label="change"
                                            size="small"
                                            style={{ marginLeft: '0.5rem' }}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    }
                    subTitle={false}
                    extra={[<NwDropDownMenu size="small" key="extra" actions={dropdownActions} />]}
                ></PageHeader>
            </InvoiceHeader>
            {showChangeAccountCodeDrawer && (
                <ChangeAccountDrawer
                    invoiceID={invoiceData.ID}
                    records={[...groupedInvoiceData]}
                    onClose={() => setShowChangeAccountCodeDrawer(false)}
                />
            )}
            {showChangeDepartmentDrawer && (
                <ChangeDepartmentDrawer
                    invoiceID={invoiceData.ID}
                    currentDepartment={invoiceData.DepartmentCreated}
                    availableDepartments={departmentsInInvoice}
                    onClose={() => setShowChangeDepartmentDrawer(false)}
                    onDepartmentChanged={() => {
                        setShowChangeDepartmentDrawer(false);
                        // You might want to reload the invoice data here
                    }}
                />
            )}
        </>
    );
};

export default InvoicePageHeader;
